import React, { useState, useEffect } from "react";
import api from "../api";
import { useNavigate } from "react-router-dom";
import { ACCESS_TOKEN, REFRESH_TOKEN } from "../constants";
import "../styles/Form.css";
import LoadingIndicator from "./LoadingIndicator";
import { FaUser, FaEnvelope, FaPhone, FaLock, FaMoon, FaSun, FaEye, FaEyeSlash } from "react-icons/fa";
import { isPaid } from "../utils/payments";

function Form({ route, method }) {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [dark, setDark] = useState(localStorage.getItem("theme") === "dark");
  const [showPassword, setShowPassword] = useState(false); // 👁️ Nuevo estado

  const name = method === "login" ? "Iniciar sesión" : "Registrarse";

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let payload = { username, password };
      if (method === "register") {
        payload.email = email;
        payload.phone = phone;
      }

      const res = await api.post(route, payload);

      if (method === "login") {
        localStorage.setItem(ACCESS_TOKEN, res.data.access);
        localStorage.setItem(REFRESH_TOKEN, res.data.refresh);
        navigate("/resumen");
      } else {
        alert("Registro exitoso 🎉");
        navigate("/login");
      }
    } catch (error) {
      if (error.response?.data) {
        alert(JSON.stringify(error.response.data));
      } else {
        alert("Error de conexión");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`min-h-screen flex items-center justify-center transition-colors duration-700
      bg-gray-100 dark:bg-gray-900`}
    >
      <div
        className="bg-white dark:bg-gray-800 shadow-2xl rounded-2xl p-8 sm:p-10 w-full max-w-md border border-gray-200 dark:border-gray-700 
        transform transition-all duration-500 hover:scale-[1.01]"
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
            {method === "register" ? "Crear cuenta" : "Bienvenido"}
          </h2>

          <button
            onClick={() => setDark(!dark)}
            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition"
            title="Alternar tema"
          >
            {dark ? <FaSun size={16} /> : <FaMoon size={16} />}
          </button>
        </div>

        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img
            src="/images/logowave.png"
            alt="WaveSales"
            className="w-28 h-28 object-contain rounded-full"
          />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Usuario */}
          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
              Usuario
            </label>
            <div className="relative">
              <FaUser className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 
                focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors duration-300"
                placeholder="Tu nombre de usuario"
              />
            </div>
          </div>

          {/* Email y Teléfono solo para registro */}
          {method === "register" && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
                  Correo electrónico
                </label>
                <div className="relative">
                  <FaEnvelope className="absolute left-3 top-3 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                    bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 
                    focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors duration-300"
                    placeholder="ejemplo@correo.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
                  Teléfono
                </label>
                <div className="relative">
                  <FaPhone className="absolute left-3 top-3 text-gray-400" />
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) =>
                      setPhone(e.target.value.replace(/[^0-9]/g, ""))
                    }
                    required
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                    bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 
                    focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors duration-300"
                    placeholder="Tu número de teléfono"
                    maxLength={20}
                  />
                </div>
              </div>
            </>
          )}

          {/* Contraseña con ojito 👁️ */}
          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
              Contraseña
            </label>
            <div className="relative">
              <FaLock className="absolute left-3 top-3 text-gray-400" />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full pl-10 pr-10 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 
                focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors duration-300"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-2.5 text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400 transition"
                title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          {loading && <LoadingIndicator />}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 
            text-white font-semibold py-2.5 rounded-lg shadow-md shadow-indigo-200/40 dark:shadow-none 
            transition-transform active:scale-95"
          >
            {loading ? "Procesando..." : name}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
          {method === "register" ? (
            <>
              ¿Ya tienes cuenta?{" "}
              <button
                onClick={() => navigate("/login")}
                className="text-indigo-600 dark:text-indigo-400 font-medium hover:underline"
              >
                Inicia sesión
              </button>
            </>
          ) : (
            <>
              ¿No tienes una cuenta?{" "}
              {isPaid() ? (
                <button
                  onClick={() => navigate("/register")}
                  className="text-indigo-600 dark:text-indigo-400 font-medium hover:underline"
                >
                  Regístrate
                </button>
              ) : (
                <button
                  onClick={() => navigate('/#pricing')}
                  className="text-gray-400 dark:text-gray-500 cursor-not-allowed"
                  title="Completa el pago en la landing para registrarte"
                >
                  Regístrate (requiere pago)
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Form;
